import { MUSIC_EVENTS } from 'Sp/Angular/Events/Client/Gallery';
import spMusicTemplate from '../../templates/components/sp-music.nghtml';

/**
 * @ngdoc component
 * @name sp.client.gallery.component:spMusic
 * @requires sp.client.common.service:SPMusicData
 * @requires $http
 * @requires $httpParamSerializer
 * @requires $element
 * @requires $scope
 * @requires spEventBus
 *
 * @description
 * Renders the music controls.
 *
 */
export default {
    template: spMusicTemplate,
    controller: [
        'SPMusicData',
        '$http',
        '$httpParamSerializer',
        '$element',
        '$scope',
        'spEventBus',
        function spMusicController(
            SPMusicData,
            $http,
            $httpParamSerializer,
            $element,
            $scope,
            spEventBus
        ) {
            const $ctrl = this;

            $ctrl.$onDestroy = $onDestroy;
            $ctrl.$onInit = $onInit;
            $ctrl.preventHideControls = false;
            $ctrl.musicIsPlaying = false;
            $ctrl.musicIsMuted = true;
            $ctrl.volumeLevel = 0.5;
            // While true, a slideshow's own music is sounding — gallery music stays ducked and
            // won't even autoplay-start until it clears.
            $ctrl.isSlideshowAudioActive = false;

            $ctrl.updateVolume = updateVolume;
            $ctrl.startOrPauseMusic = startOrPauseMusic;

            function $onInit() {
                if (SPMusicData.getAsync) {
                    SPMusicData.getAsync().then((data) => {
                        setupMusic(data);
                    });
                } else if (SPMusicData.get) {
                    setupMusic(SPMusicData.get());
                }

                $ctrl.offMusicMute = spEventBus.on(MUSIC_EVENTS.MUTE, muteForSlideshow);
                $ctrl.offMusicUnmute = spEventBus.on(MUSIC_EVENTS.UNMUTE, unmuteAfterSlideshow);
            }

            // Triggered by the slideshow via spEventBus, i.e. from outside Angular's digest cycle
            // (React doesn't know about $scope.$apply) — without forcing a digest here, the
            // ng-if-bound play/mute icons wouldn't refresh until some unrelated Angular event
            // (e.g. a mouseenter) happened to trigger one.
            function muteForSlideshow() {
                $ctrl.isSlideshowAudioActive = true;

                if ($ctrl.currentAudio && $ctrl.musicIsPlaying) {
                    $ctrl.currentAudio.pause();
                    $ctrl.musicIsPlaying = false;
                    $ctrl.musicIsMuted = true;
                    $scope.$applyAsync();
                }
            }

            // Only clears the "slideshow is using audio" bookkeeping — deliberately does not
            // resume gallery music. Only ever starting (never resuming) the other side keeps
            // gallery and slideshow music mutually exclusive without either fighting back.
            function unmuteAfterSlideshow() {
                $ctrl.isSlideshowAudioActive = false;
            }

            function $onDestroy() {
                $ctrl.musicIsPlaying = false;
                $ctrl.musicIsMuted = true;
                $ctrl.currentAudio.pause();
                $ctrl.currentAudio.removeEventListener('ended', $ctrl.loopMusic);
                $ctrl.currentAudio = null;
                $ctrl.offMusicMute?.();
                $ctrl.offMusicUnmute?.();
            }

            function updateVolume(level) {
                $ctrl.volumeLevel = level;
                $ctrl.currentAudio.volume = level;
            }

            function startOrPauseMusic() {
                if ($ctrl.musicIsPlaying) {
                    $ctrl.currentAudio.pause();
                    $ctrl.musicIsPlaying = false;
                    $ctrl.musicIsMuted = true;
                } else {
                    $ctrl.currentAudio.play();
                    $ctrl.musicIsPlaying = true;
                    $ctrl.musicIsMuted = false;
                    // Mirror image of MUTE: tell any open slideshow to pause instead of playing
                    // over the gallery music the visitor just started.
                    spEventBus.emit(MUSIC_EVENTS.GALLERY_PLAYING);

                    SPMusicData.setCurrentSongId($ctrl.currentSong.id);
                }
            }

            function setupMusic(musicData) {
                if (
                    !musicData.isMusicAllowed ||
                    ((typeof musicData.playlistSongs === undefined &&
                        musicData.playlistSongs.length > 0) ||
                        musicData.playlistSongs === false)
                ) {
                    return;
                }

                const autoPlay = musicData.musicAutoPlay;
                const playlistSongs = musicData.playlistSongs;
                const musicAuthToken = musicData.musicTsmAuthToken;
                let currentSongIndex = 0;

                $ctrl.addEventEnded = () => {
                    $ctrl.currentAudio.addEventListener('ended', $ctrl.loopMusic);
                };

                $ctrl.loopMusic = () => {
                    $ctrl.currentAudio.currentTime = 0;

                    $http.post(
                        `https://api.triplescoopmusic.com/v1/analytics/songs/${
                            playlistSongs[currentSongIndex].id
                        }/play`,
                        $httpParamSerializer({
                            authToken: musicAuthToken
                        }),
                        {
                            headers: {
                                'Content-Type': 'application/x-www-form-urlencoded'
                            }
                        }
                    );

                    const nextSongIndex = currentSongIndex + 1;

                    if (!playlistSongs[nextSongIndex]) {
                        $ctrl.currentSong = playlistSongs[0];
                        SPMusicData.setCurrentSongId($ctrl.currentSong.id);

                        $ctrl.currentAudio = new Audio([$ctrl.currentSong.m]);
                        $ctrl.addEventEnded();
                        $ctrl.currentAudio.play();
                        currentSongIndex = 0;
                    } else {
                        $ctrl.currentSong = playlistSongs[nextSongIndex];
                        SPMusicData.setCurrentSongId($ctrl.currentSong.id);

                        $ctrl.currentAudio = new Audio([$ctrl.currentSong.m]);
                        $ctrl.addEventEnded();
                        $ctrl.currentAudio.play();
                        currentSongIndex++;
                    }
                };

                $ctrl.currentSong = playlistSongs[0];
                $ctrl.currentAudio = new Audio([$ctrl.currentSong.m]);
                $ctrl.addEventEnded();
                SPMusicData.setCurrentSongId($ctrl.currentSong.id);
                $element.addClass('ready');

                if (
                    autoPlay &&
                    !$ctrl.isSlideshowAudioActive &&
                    $ctrl.currentAudio &&
                    $ctrl.currentAudio.paused
                ) {
                    const promise = $ctrl.currentAudio.play();

                    if (promise !== undefined) {
                        promise
                            .then(() => {
                                $ctrl.currentAudio.play();
                                $ctrl.musicIsPlaying = true;
                                $ctrl.musicIsMuted = false;
                                spEventBus.emit(MUSIC_EVENTS.GALLERY_PLAYING);
                            })
                            .catch(() => {
                                $ctrl.musicIsPlaying = false;
                                $ctrl.musicIsMuted = true;
                            });
                    }
                }
            }
        }
    ]
};
